import React, { useMemo, useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import "rc-time-picker/assets/index.css";
import { Modal, ModalBody } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import styles from "./Credentials.module.scss";
import userDefaultImage from "assets/images/staff-default-rounded.png";
import Select from "react-select";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import { useAllStaffByDesignationIdAndOfficeId } from "repositories/notes-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import Loader from "components/Loader";
import constants from "../../constants";

function EmployeeSelection({
  t,
  issaveEmployeeModalOpen,
  setIsSaveEmployeeModalOpen,
  memberIds,
  setMemberIds,
  ownerId,
  hideFilter = true,
}) {
  const { commonOwnerData } = useAccountOwnerCommon();
  const defaultOfficeValue = useMemo(
    () => ({
      value: 0,
      label: t("accountOwner.allOffices"),
    }),
    [t],
  );
  const defaultRoleValue = useMemo(
    () => ({
      value: 0,
      label: t("accountOwner.allRoles"),
    }),
    [t],
  );
  const [search, setSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState(memberIds);
  const [selectedOffice, setSelectedOffice] = useState(defaultOfficeValue);
  const [selectedRole, setSelectedRole] = useState(defaultRoleValue);
  const { isLoading: loadingRoleData, data: staffRoleData } =
    useStaffDesignation();

  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(1, constants.ACTIVE_OFFICES_PAGE_SIZE, {
      ownerId,
      includeAdminOffice: true,
    });

  const { selectedOfficeFilter, selectedRoles } = useMemo(() => {
    return {
      selectedRoles: selectedRole?.value ? [selectedRole?.value] : [],
      selectedOfficeFilter: selectedOffice?.value
        ? [selectedOffice?.value]
        : [],
    };
  }, [selectedRole?.value, selectedOffice?.value]);
  const { data: staffListData, isLoading: staffListDataLoading } =
    useAllStaffByDesignationIdAndOfficeId(
      selectedRoles,
      selectedOfficeFilter,
      { ownerId },
      { enabled: !!ownerId },
    );
  const rolesData = useMemo(() => {
    if (!loadingRoleData && staffRoleData?.length) {
      const flatData = staffRoleData?.map((item) => item?.designations)?.flat();
      if (flatData?.length) {
        const data = flatData?.map((item) => {
          return {
            label: item?.name,
            value: item?.id,
          };
        });
        data?.unshift({
          value: defaultRoleValue.value,
          label: defaultRoleValue.label,
        });
        return data;
      }
    }
    return [];
  }, [loadingRoleData, staffRoleData, defaultRoleValue]);
  const officeList = useMemo(() => {
    if (!officeDataLoading && officeData?.data?.length) {
      const data = officeData?.data?.map((res) => ({
        value: res.id,
        label: res.name,
      }));
      data?.unshift({
        value: defaultOfficeValue.value,
        label: defaultOfficeValue.label,
      });
      return data;
    }
    return [];
  }, [officeDataLoading, officeData?.data, defaultOfficeValue]);
  const staffList = useMemo(() => {
    if (!staffListDataLoading && staffListData?.data?.length) {
      const list = [];
      const seenIds = new Map();

      // Add owner data if available
      const ownerData = commonOwnerData;
      if (ownerData && ownerId && ownerData.id === ownerId) {
        const ownerItem = {
          userId: ownerData.id,
          user: {
            id: ownerData.id,
            firstName: ownerData.firstName || "",
            lastName: ownerData.lastName || "",
            profilePic: ownerData.profilePic || null,
          },
        };
        list.push(ownerItem);
        seenIds.set(ownerData.id, true);
      }

      // Add staff data
      (issaveEmployeeModalOpen === "office"
        ? staffListData.data.filter((res) => res.isAdmin)
        : staffListData.data
      ).forEach((res) => {
        if (!seenIds.has(res.userId)) {
          seenIds.set(res.userId, true);
          list.push(res);
        }
      });
      return list;
    }
    return [];
  }, [staffListDataLoading, staffListData, commonOwnerData, ownerId]);
  const filteredStaffList = useMemo(() => {
    const trimSearch = search?.trim().toLowerCase();
    if (trimSearch.length && staffList?.length) {
      return staffList?.filter((res) => {
        return `${res.user?.firstName} ${res.user?.lastName}`
          ?.toLowerCase()
          .includes(trimSearch);
      });
    }
    return staffList;
  }, [staffList, search]);
  const onSearch = (val) => {
    setSearch(val);
  };

  const checkMember = (e, member) => {
    if (e.target.checked) {
      setSelectedMembers([...selectedMembers, member]);
    } else {
      setSelectedMembers(
        selectedMembers.filter((res) => res.userId !== member.userId),
      );
    }
  };

  const addDefaultSrc = (ev) => {
    ev.target.src = userDefaultImage;
    ev.target.onerror = null;
  };

  const checkMemberRoleAvailable = (array, value) => {
    return array.some((e) => e.userId === value.userId);
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked && filteredStaffList.length > 0) {
      setSelectedMembers(filteredStaffList);
    } else {
      setSelectedMembers([]);
    }
  };

  return (
    <Modal
      isOpen={issaveEmployeeModalOpen}
      toggle={() => setIsSaveEmployeeModalOpen(false)}
      className="modal-dialog-centered modal-lg"
      modalClassName="custom-modal"
    >
      <span
        className="close-btn"
        onClick={() => setIsSaveEmployeeModalOpen(false)}
      >
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody className={styles["add-employee-modal-dialog"]}>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {t("accountOwner.selectEmployees")}
          </span>
        </Text>
        <div className={"search-box w-100 " + styles["search-box"]}>
          <input
            type="text"
            placeholder={t("accountOwner.searchEmployee")}
            value={search}
            onChange={(e) => onSearch(e.currentTarget.value)}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>

        {hideFilter && (
          <div
            className={"w-100 d-flex gap-xs " + styles.employeeSelectionSelect}
          >
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={officeList}
              value={selectedOffice}
              onChange={(val) => setSelectedOffice(val ?? defaultOfficeValue)}
              isSearchable={false}
            />
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={rolesData}
              value={selectedRole}
              onChange={(val) => setSelectedRole(val ?? null)}
              isSearchable={false}
            />
          </div>
        )}

        <Text
          size="12px"
          marginBottom="10px"
          weight="400"
          color="rgb(111, 119, 136)"
          marginTop="30px"
        >
          {selectedMembers?.length} {t("accountOwner.selected")}
        </Text>

        <div className="ch-checkbox">
          <label>
            <input
              type="checkbox"
              checked={
                selectedMembers.length > 0 &&
                filteredStaffList.every((member) =>
                  selectedMembers.some(
                    (selected) => selected.userId === member.userId,
                  ),
                )
              }
              onChange={toggleSelectAll}
            />
            <span> {t("staffTimesheet.selectAll")}</span>
          </label>
        </div>
        {staffListDataLoading && <Loader />}
        <ul className={"modal-employee-list " + styles["employee-list"]}>
          {filteredStaffList.length > 0 &&
            filteredStaffList.map((val) => {
              return (
                <li key={val.userId}>
                  <div className="ch-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={checkMemberRoleAvailable(selectedMembers, val)}
                        onChange={(e) => checkMember(e, val)}
                      />
                      <span className="d-flex align-items-center">
                        <img
                          src={val.user?.profilePic || userDefaultImage}
                          onError={addDefaultSrc}
                          alt="profile"
                        />
                        <div>
                          {val.user?.firstName} {val.user?.lastName}{" "}
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })}
          {filteredStaffList.length === 0 && (
            <li>
              <div className="ch-checkbox">
                <label>{t("staffLeaves.noStaffAdded")}</label>
              </div>
            </li>
          )}
        </ul>

        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100"
          title={t("accountOwner.addEmployees")}
          onClick={() => {
            setMemberIds(selectedMembers);
            setIsSaveEmployeeModalOpen(false);
          }}
        >
          {t("questionnaire.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3"
          onClick={() => setIsSaveEmployeeModalOpen(false)}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(EmployeeSelection);
